import { type ReactNode } from 'react';

import { Box, type SxProps, type Theme } from '@mui/material';

export type CardLayout = Readonly<{
  /** `grid-template-columns` above the breakpoint. */
  columns: string;
  /** `grid-template-areas` above the breakpoint. Naming an area is what renders its card. */
  areas: string;
  /** Overrides the one-area-per-row stack used below the breakpoint. */
  stackedAreas?: string;
}>;

/** The area names a template uses, first appearance first. `.` placeholders drop out. */
const areaNames = (areas: string): string[] => {
  const found: string[] = areas.match(/[\w-]+/g) ?? [];

  return found.filter((name, index) => found.indexOf(name) === index);
};

// A template has to be given for the single-column case too: an item keeps its
// `grid-area` name, and with no area to match it lands on top of its siblings
// rather than flowing into its own row.
const stackedAreasOf = (areas: string): string =>
  areaNames(areas)
    .map((name) => `"${name}"`)
    .join(' ');

const gridSx =
  (layout: CardLayout): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridTemplateAreas: layout.stackedAreas ?? stackedAreasOf(layout.areas),
    gap: backrs.spacing['spacing-3xl'],
    alignItems: 'stretch',
    '@container (min-width: 900px)': {
      gridTemplateColumns: layout.columns,
      gridTemplateAreas: layout.areas,
    },
  });

/**
 * Arranges analytics cards by name. `layout.areas` decides both where each card
 * sits and whether it appears at all — a card with no area in the template is
 * not rendered, and an area with no card leaves its space empty.
 */
export const AnalyticsCardGrid = ({
  layout,
  cards,
}: Readonly<{ layout: CardLayout; cards: Readonly<Record<string, ReactNode>> }>) => (
  <Box sx={gridSx(layout)}>
    {areaNames(layout.areas).map((name) =>
      cards[name] ? (
        <Box key={name} sx={{ display: 'flex', gridArea: name }}>
          {cards[name]}
        </Box>
      ) : null,
    )}
  </Box>
);
